import { useEffect, useState, useCallback } from 'react';
import { useFavorites } from '../contexts/FavoritesContext';
import MealImage from '../components/MealImage';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Clock, Users, Heart, Plus, X, Check, Edit3, RotateCcw, Calendar, AlertTriangle, Search, ChevronDown, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mealAPI, mealPlanAPI, logFoodAPI } from '../utils/api';
import { toFraction } from '../utils/fractions';
import { formatMacro } from '../utils/nutritionFormat';
import { toast } from 'sonner';
import WeekDateSelector from '../components/WeekDateSelector';
import { getTodayString, formatDateString, resolveLoggingDate, formatFriendlyDate } from '../utils/dateUtils';
import { useToday } from '../hooks/useToday';
import useKeyboardInset from '../hooks/useKeyboardInset';
import { safeReturnPath, finishFlow } from '../utils/planLocation';
import { pendingSnackMealIds } from '../utils/guidedNextStep';
import { readSwapContext, performSwap, swapErrorMessage } from '../utils/swapAction';

// Placeholder image for when meal image fails or is missing
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&h=600&fit=crop';

// Defensive display safeguard: when an ingredient row has NO trusted household
// conversion (its display unit is grams), some legacy/stale catalog records keep
// the original household amount embedded in the name — e.g. "Sweet potatoes
// (1.5 cups)". Showing that alongside a scaled grams amount ("480 g") is
// contradictory. Strip the embedded measurement so a single clean grams amount
// is shown. This does NOT invent a household conversion; it only removes the
// conflicting embedded text. Trusted household units are left untouched.
function stripEmbeddedMeasure(name, unitId) {
  if (!name) return name;
  var u = String(unitId || '').toLowerCase();
  var isGrams = (u === '' || u === 'g' || u === 'gram' || u === 'grams');
  if (!isGrams) return name; // household unit is trustworthy — keep as-is
  return name.replace(
    /\s*\((?=[^)]*\d)[^)]*\b(cups?|tbsps?|tsps?|tablespoons?|teaspoons?|oz|ounces?|cloves?|slices?|scoops?|ml|l|kg|g|grams?)\b[^)]*\)\s*$/i,
    ''
  ).trim();
}

// CSS to hide number input spinners
const hideSpinnerStyles = `
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }
`;

function MealView() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mealId = params.mealId;
  
  // Context from location state (passed from Home page when clicking meal card)
  const locationState = location.state || {};
  const contextMealTypeFromState = locationState.mealType || '';
  
  // Guided context from URL params
  const guidedMode = searchParams.get('guided') === 'true';
  const contextDate = searchParams.get('date') || getTodayString();
  // Reactive LOCAL current day (same source as Home). Used ONLY for the current-day log action
  // (Log / Log Again) via resolveLoggingDate; planning/date-picker flows keep their own dates.
  const liveToday = useToday();
  const contextMealType = searchParams.get('type') || contextMealTypeFromState || '';
  // Opened from a Swap browser: this detail is part of that Swap, so its
  // action swaps (never adds a second row) and finishes the whole Swap at the
  // Swap's own origin (flowReturn), not at the browser.
  const swapCtx = readSwapContext(searchParams);
  const swapFlowReturn = safeReturnPath(searchParams.get('flowReturn'),
    safeReturnPath(searchParams.get('return'), '/plan'));
  // After an add / log, go back to the screen that opened this detail when it
  // is known to be the previous entry (Plan, a browser, Step-by-Step);
  // otherwise keep the legacy destination.
  const leaveAfterAdd = function(legacyPath) {
    if (location.state && location.state.fxFlowBack) navigate(-1);
    else navigate(legacyPath);
  };
  // Plan ID for editing existing plan entries
  const planId = searchParams.get('planId') || null;
  // Explicit, unsaved Step-by-Step candidate context. Guided browsing alone
  // is not enough to make an ordinary Meal View read-only.
  const guidedCandidateMode = guidedMode && searchParams.get('candidate') === '1' && !planId;
  // A candidate from Step-by-Step's Browse All: its Snack step keeps the
  // Step-by-Step duplicate-Snack protection.
  const stepBrowseCandidate = guidedCandidateMode && searchParams.get('stepBrowse') === '1';
  // Status of the plan entry (completed = already logged)
  const navStatus = searchParams.get('status') || '';
  // Scale factor from plan (for scaled meals)
  const scaleFactor = parseFloat(searchParams.get('scaleFactor')) || 1.0;
  const scaleLabel = searchParams.get('scaleLabel') || '';
  const isScaled = scaleFactor !== 1.0;
  // LEFTOVERS: prep (batch) vs consumption (per-day). Ingredient display on the
  // cook card uses prep_scale_factor/recipe.servings (== grocery math); macros
  // always use the per-day scaleFactor.
  const leftoverRole = searchParams.get('leftoverRole') || '';
  const prepScaleFactor = parseFloat(searchParams.get('prepScaleFactor')) || 0;
  // CANONICAL leftover/scaling context resolved from BACKEND plan data (robust
  // to null/legacy prep_scale_factor and old deep-link URLs missing params).
  const [planContext, setPlanContext] = useState(null);
  const ctxRole = (planContext && planContext.leftover_role != null) ? planContext.leftover_role : leftoverRole;
  const isCookMeal = ctxRole === 'cook';
  const isLeftoverMeal = ctxRole === 'leftover';
  // Ingredient display multiplier. Cook rows use the backend canonical
  // ingredient_multiplier (== prep_scale_factor / R == grocery math); falls
  // back to query param, then per-day scaleFactor. `ctxOverride` lets the load
  // effect pass the freshly-fetched context synchronously (before state set).
  const computeIngScale = function(recipeServings, ctxOverride) {
    const ctx = ctxOverride || planContext;
    const role = (ctx && ctx.leftover_role != null) ? ctx.leftover_role : leftoverRole;
    // Cook + leftover rows use the backend-canonical multiplier when available
    // (cook = ΣS/R == grocery; leftover = S2/R, that day's revealed portion).
    if ((role === 'cook' || role === 'leftover') && ctx && ctx.ingredient_multiplier > 0) {
      return ctx.ingredient_multiplier;
    }
    const R = recipeServings || (ctx && ctx.recipe_servings) || 0;
    if (role === 'cook' && prepScaleFactor > 0 && R > 0) return prepScaleFactor / R;
    if (role === 'leftover' && R > 0) return scaleFactor / R;
    return scaleFactor;
  };
  // Authoritative linked status (backend context; query param only as fallback).
  const isLinked = planContext ? !!planContext.is_linked
    : (leftoverRole === 'cook' || leftoverRole === 'leftover');
  
  const [meal, setMeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isFavorite: favHas, toggleFavorite: ctxToggleFavorite } = useFavorites();
  const [imageError, setImageError] = useState(false);
  const [showAddToPlan, setShowAddToPlan] = useState(false);
  const [addingToPlan, setAddingToPlan] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  
  // (isAlreadyLogged / isPlannedRow / logDate are derived below with the
  // logging-date rules; the Add to Plan modal keeps its own planDate.)

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [ingredientAdjustments, setIngredientAdjustments] = useState([]);
  const [editedMacros, setEditedMacros] = useState(null);
  const [calculatingMacros, setCalculatingMacros] = useState(false);
  
  // Add ingredient state
  const [showAddIngredient, setShowAddIngredient] = useState(false);
  // Lift the fixed Add Ingredient sheet above the iOS keyboard while its search field is typed in (same
  // as the AI log FoodSearchSheet); the sheet returns to the bottom when the keyboard closes.
  const kb = useKeyboardInset();
  const [ingredientSearchQuery, setIngredientSearchQuery] = useState('');
  const [ingredientSearchResults, setIngredientSearchResults] = useState([]);
  const [searchingIngredients, setSearchingIngredients] = useState(false);
  const [showLeftoverRecipe, setShowLeftoverRecipe] = useState(false);
  // Canonical-context failure for a linked row -> show safe retry, never a
  // silent (potentially wrong) one-portion recipe. reloadKey drives retry.
  const [contextError, setContextError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showUnitPicker, setShowUnitPicker] = useState(null); // Index of ingredient showing unit picker
  const [loadingUnitOptions, setLoadingUnitOptions] = useState(null); // Index of ingredient loading options
  
  // Hydrate USDA portion options for a custom meal ingredient when dropdown opens
  const hydratePortionOptions = async function(index) {
    const ingredient = ingredientAdjustments[index];
    if (!ingredient) return;
    
    // Already has real options (admin meals) — skip
    var opts = ingredient.portionOptions || [];
    if (opts.length > 1) return;
    
    // Need a food_id to fetch
    var foodId = ingredient.food_id || ingredient.id;
    if (!foodId || !(foodId + '').startsWith('sp_ing_')) return;
    
    setLoadingUnitOptions(index);
    try {
      var res = await logFoodAPI.getFoodNutrition(foodId, 1, ingredient.displayUnitId || 'g');
      var options = res.data?.serving_options || [];
      var newPortionOptions = options.map(function(opt) {
        return { id: opt.unit, label: opt.unit, grams_per_unit: 1, is_estimate: false };
      });
      if (newPortionOptions.length > 0) {
        setIngredientAdjustments(function(prev) {
          var updated = prev.slice();
          updated[index] = Object.assign({}, updated[index], {
            portionOptions: newPortionOptions
          });
          return updated;
        });
      }
    } catch (err) {
      console.error('[MealView] Failed to hydrate portion options:', err);
    } finally {
      setLoadingUnitOptions(null);
    }
  };
  
  // Add to Plan / Add to Different Day: the modal owns its OWN planning date.
  // It never feeds the Log / Log Again action below; opening, changing or
  // dismissing the modal leaves the Log CTA exactly as it was.
  const [planDate, setPlanDate] = useState(contextDate);

  // LOGGING date — derived from the logging context only (never the modal).
  //   rowDate:   the saved plan row's own date. Callers pass it as ?date= via
  //              planRowDate() (the plan-context endpoint does not return it).
  //   rowStatus: the authoritative saved status from the plan context when
  //              loaded, else the ?status= the caller passed. A stale URL
  //              "pending" can never re-update a row the server says is logged.
  //   A pending/planned row is completed ONLY in its own saved date context.
  //   Everything else (fresh log, Log Again) creates a NEW completed row on the
  //   resolved logging date: ?current=1 follows the reactive local day (Log
  //   Again after midnight lands on the new day); a fixed ?date= is preserved.
  const rowDate = contextDate;
  const rowStatus = (planContext && planContext.status) || navStatus;
  const isPlannedRow = !!(planId && rowStatus !== 'completed');
  const isAlreadyLogged = !!(planId && rowStatus === 'completed');
  const logDate = isPlannedRow
    ? rowDate
    : resolveLoggingDate({
        explicitDate: searchParams.get('date'),
        currentFlag: searchParams.get('current'),
        liveToday,
        missingMeansCurrent: false,
      });
  const logDateLabel = formatFriendlyDate(logDate);
  const rowDateLabel = formatFriendlyDate(rowDate);
  const [selectedMealSlot, setSelectedMealSlot] = useState(contextMealType || '');
  const [addToPlanStep, setAddToPlanStep] = useState(1); // 1: date, 2: slot
  const [existingMealInSlot, setExistingMealInSlot] = useState(null);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  useEffect(() => {
    async function loadMeal() {
      if (!mealId) {
        setError('No meal ID provided');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      setContextError(false);
      
      // Detect meal type based on ID prefix
      const isCustomMeal = (
        mealId.startsWith('custom_') || 
        (mealId.startsWith('meal_') && !mealId.startsWith('meal_v2_'))
      );
      const isAdminMeal = mealId.startsWith('meal_v2_');
      
      try {
        let mealData = null;
        
        // Check if this plan has user edits (custom macros/ingredients)
        // NOTE: getMealPlanEdits is exported from mealPlanAPI (NOT mealAPI).
        // The previous call to mealAPI.getMealPlanEdits silently threw a
        // TypeError swallowed by this try/catch, which caused every reopen
        // of an edited logged meal to fall back to the base template.
        let planEdits = null;
        if (planId) {
          try {
            const editsResponse = await mealPlanAPI.getMealPlanEdits(planId);
            if (editsResponse.data && editsResponse.data.is_edited) {
              planEdits = editsResponse.data;
            }
          } catch (e) {
            // Plan edits not found — proceed with base meal
          }
        }

        // CANONICAL leftover/scaling context (backend-derived). Fetched here so
        // the initial ingredient build uses the canonical multiplier even on a
        // cold deep link / refresh where query params may be missing or stale.
        // For a LINKED row we MUST NOT fall back to a one-portion recipe if this
        // fails — show a safe retry instead of wrong preparation quantities.
        let ctx = null;
        if (planId) {
          try {
            const ctxResp = await mealPlanAPI.getMealPlanContext(planId);
            ctx = ctxResp.data || null;
            setPlanContext(ctx);
            // Linked row with a malformed/invalid group -> never show possibly
            // wrong quantities; surface the safe error/retry state instead.
            if (ctx && ctx.is_linked && ctx.group_valid === false) {
              setContextError(true);
              setLoading(false);
              return;
            }
          } catch (e) {
            // Any saved-plan context failure is a SAFE error/retry state — we
            // never fall through to (possibly wrong) fallback quantities for a
            // plan row we couldn't authoritatively resolve.
            setContextError(true);
            setLoading(false);
            return;
          }
        }
        
        if (isAdminMeal) {
          // Admin meal - fetch from main meals endpoint
          const response = await mealAPI.getMeal(mealId);
          mealData = response.data;
        } else if (isCustomMeal) {
          // Custom meal - try custom meals endpoint first
          try {
            const response = await mealAPI.getCustomMeal(mealId, planId);
            mealData = response.data;
          } catch (customErr) {
            // Fallback to URL params for meals that don't exist in DB
            const urlName = searchParams.get('name');
            const urlCalories = searchParams.get('calories');
            if (urlName && urlCalories) {
              mealData = {
                meal_id: mealId,
                name: urlName,
                calories: parseInt(urlCalories) || 0,
                protein: parseFloat(searchParams.get('protein')) || 0,
                carbs: parseFloat(searchParams.get('carbs')) || 0,
                fat: parseFloat(searchParams.get('fat')) || 0,
                source: 'URL_PARAMS',
                is_custom: true
              };
            } else {
              throw customErr;
            }
          }
          // Mark as custom meal
          mealData.is_custom = true;
        } else {
          // Unknown type (sp_prod_*, sp_ing_*, upc_*, etc.) — try main endpoint, fallback to URL params
          try {
            const response = await mealAPI.getMeal(mealId);
            mealData = response.data;
          } catch (err) {
            // Fallback to URL params for logged foods not in meals DB
            const urlName = searchParams.get('name');
            const urlCalories = searchParams.get('calories');
            if (urlName && urlCalories) {
              mealData = {
                meal_id: mealId,
                name: urlName,
                calories: parseInt(urlCalories) || 0,
                protein: parseFloat(searchParams.get('protein')) || 0,
                carbs: parseFloat(searchParams.get('carbs')) || 0,
                fat: parseFloat(searchParams.get('fat')) || 0,
                source: 'URL_PARAMS',
                is_custom: true
              };
            } else {
              throw err;
            }
          }
        }
        
        // Apply scale factor to top-level macros if this is a scaled plan entry
        // BUT NOT if the plan has user edits — those are already final
        if (planEdits && planEdits.macros) {
          // Use edited macros directly — these are the user's final values
          mealData = Object.assign({}, mealData, {
            calories: Math.round(planEdits.macros.calories || 0),
            protein: Math.round(planEdits.macros.protein || 0),
            carbs: Math.round(planEdits.macros.carbs || 0),
            fat: Math.round(planEdits.macros.fat || 0),
            _is_plan_edited: true,
          });
        } else if (scaleFactor !== 1.0 && !mealData.is_custom) {
          mealData = Object.assign({}, mealData, {
            calories: Math.round((mealData.calories || 0) * scaleFactor),
            protein: Math.round((mealData.protein || 0) * scaleFactor),
            carbs: Math.round((mealData.carbs || 0) * scaleFactor),
            fat: Math.round((mealData.fat || 0) * scaleFactor),
          });
        }
        
        setMeal(mealData);
        
        // Initialize ingredient adjustments based on meal type
        // If plan has custom ingredients from a previous edit, use those.
        // SAFEGUARD: stored ingredients only contain {name, quantity, unit,
        // grams, macros, is_new, _perG?, portionOptions?, food_id?, id?}.
        // We must hydrate them back into the full editor shape so the meal
        // detail page displays the edited quantities and units (not blanks
        // or fallback originals).
        if (planEdits && planEdits.custom_ingredients && planEdits.custom_ingredients.length > 0) {
          setIngredientAdjustments(planEdits.custom_ingredients.map(function(adj, idx) {
            var displayQty = (adj.displayQty != null ? adj.displayQty : adj.quantity);
            if (displayQty == null) displayQty = 1;
            var displayUnitId = adj.displayUnitId || adj.unit || 'g';
            var grams = adj.grams || 0;
            var perG = adj._perG || { cal: 0, protein: 0, carbs: 0, fat: 0 };
            var macros = adj.macros || { calories: 0, protein: 0, carbs: 0, fat: 0 };
            var portionOptions = adj.portionOptions && adj.portionOptions.length > 0
              ? adj.portionOptions
              : [{ id: displayUnitId, label: displayUnitId, grams_per_unit: 1, is_estimate: true }];
            return Object.assign({}, adj, {
              id: adj.id || adj.food_id || ('custom_' + idx),
              food_id: adj.food_id || null,
              name: adj.name || '',
              displayQty: displayQty,
              displayUnitId: displayUnitId,
              grams: grams,
              original_displayQty: displayQty,
              original_displayUnitId: displayUnitId,
              original_grams: grams,
              _perG: perG,
              macros: macros,
              portionOptions: portionOptions,
              removed: false,
              is_text_only: !adj.food_id && (!adj.portionOptions || adj.portionOptions.length <= 1) && (!perG.cal && !perG.protein),
            });
          }));
          // Wrap raw macros into the same shape produced by calculateEditedMacros
          // so display code (currentMacros = editedMacros.adjusted_macros) and
          // save/log code work consistently for reloaded edits.
          setEditedMacros({
            meal_id: mealId,
            original_macros: {
              calories: (mealData && mealData.calories) || 0,
              protein: (mealData && mealData.protein) || 0,
              carbs: (mealData && mealData.carbs) || 0,
              fat: (mealData && mealData.fat) || 0,
            },
            adjusted_macros: {
              calories: Math.round((planEdits.macros && planEdits.macros.calories) || 0),
              protein: Math.round((planEdits.macros && planEdits.macros.protein) || 0),
              carbs: Math.round((planEdits.macros && planEdits.macros.carbs) || 0),
              fat: Math.round((planEdits.macros && planEdits.macros.fat) || 0),
            },
            adjusted_ingredients: planEdits.custom_ingredients,
            is_edited: true,
          });
        } else if (mealData.is_custom) {
          // Custom meals: prefer structured ingredient_quantities over text-only ingredients
          const customIngredients = (mealData.ingredient_quantities && mealData.ingredient_quantities.length > 0)
            ? mealData.ingredient_quantities
            : (mealData.ingredients || []);
          setIngredientAdjustments(customIngredients.map(function(ing, idx) {
            // Handle both string and object formats
            if (typeof ing === 'string') {
              return {
                id: 'custom_' + idx,
                name: ing,
                displayQty: 1,
                displayUnitId: 'serving',
                grams: 0,
                original_displayQty: 1,
                original_displayUnitId: 'serving',
                original_grams: 0,
                _perG: { cal: 0, protein: 0, carbs: 0, fat: 0 },
                macros: { calories: 0, protein: 0, carbs: 0, fat: 0 },
                portionOptions: [{ id: 'serving', label: 'serving', grams_per_unit: 1, is_estimate: true }],
                removed: false,
                is_text_only: true
              };
            }
            return {
              id: ing.core_food_id || ing.food_id || 'custom_' + idx,
              food_id: ing.food_id || null,
              name: ing.name || ing.text || '',
              prep_note: ing.prep_note || '',
              displayQty: ing.quantity || 1,
              displayUnitId: ing.unit || 'serving',
              grams: ing.grams || 0,
              original_displayQty: ing.quantity || 1,
              original_displayUnitId: ing.unit || 'serving',
              original_grams: ing.grams || 0,
              _perG: ing._perG || { cal: 0, protein: 0, carbs: 0, fat: 0 },
              macros: ing.macros || { calories: ing.calories || 0, protein: ing.protein || 0, carbs: ing.carbs || 0, fat: ing.fat || 0 },
              portionOptions: ing.portion_options || [{ id: ing.unit || 'serving', label: ing.unit || 'serving', grams_per_unit: ing.grams || 1, is_estimate: true }],
              removed: false,
              is_text_only: !ing.food_id && !ing.portion_options
            };
          }));
        } else {
          // Admin meals have USDA structured ingredients
          // P3B: drop allergen-omitted rows BEFORE the existing scaling below.
          // Use the freshly-fetched ctx (planContext state may not be hydrated
          // yet on this initial mealId/reloadKey run).
          const omissionCtx = ctx || planContext;
          const _omitted = new Set(((omissionCtx && omissionCtx.omitted_ingredient_ids) || []).map(String));
          const ingredients = (mealData.ingredient_quantities || []).filter(function(ing) {
            return !_omitted.has(String(ing.ingredient_id));
          });
          setIngredientAdjustments(ingredients.map(function(ing) {
            var qty = ing.quantity;
            try {
              qty = parseFloat(ing.quantity) || 1;
            } catch (e) {
              qty = 1;
            }
            
            // Apply scale factor to grams and display quantity.
            // Cook card uses the batch (prep) multiplier for ingredients.
            var _ingScale = computeIngScale(mealData.servings, ctx);
            var scaledGrams = (ing.grams || 0) * _ingScale;
            var scaledQty = qty * _ingScale;
            
            // Store per-gram nutrition from backend (same as admin)
            const perGramNutrition = {
              cal_per_g: ing.cal_per_g || 0,
              protein_per_g: ing.protein_per_g || 0,
              carbs_per_g: ing.carbs_per_g || 0,
              fat_per_g: ing.fat_per_g || 0
            };
            
            // Calculate current macros for this ingredient based on SCALED grams
            const macros = {
              calories: Math.round(scaledGrams * perGramNutrition.cal_per_g),
              protein: Math.round(scaledGrams * perGramNutrition.protein_per_g),
              carbs: Math.round(scaledGrams * perGramNutrition.carbs_per_g),
              fat: Math.round(scaledGrams * perGramNutrition.fat_per_g)
            };
            
            // Get portion options from backend (same format as admin meal editor)
            const portionOptions = ing.portion_options || [
              { id: 'g', label: 'grams', grams_per_unit: 1, is_estimate: false }
            ];
            
            return {
              id: ing.core_food_id || '',
              name: ing.name || '',
              prep_note: ing.prep_note || '',
              displayQty: scaledQty,
              displayUnitId: ing.unit || 'g',
              grams: scaledGrams,
              original_displayQty: scaledQty,
              original_displayUnitId: ing.unit || 'g',
              original_grams: scaledGrams,
              _perG: {
                cal: perGramNutrition.cal_per_g,
                protein: perGramNutrition.protein_per_g,
                carbs: perGramNutrition.carbs_per_g,
                fat: perGramNutrition.fat_per_g
              },
              macros: macros,
              portionOptions: portionOptions,
              removed: false,
              isOptional: ing.is_optional || false
            };
          }));
        }
        
      } catch (err) {
        console.error('Error loading meal:', err);
        setError('Failed to load meal details. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    
    loadMeal();
  }, [mealId, reloadKey]);

  // Calculate edited macros when adjustments change - PURELY CLIENT-SIDE
  // Uses the SAME methodology as Admin Meal Editor
  const calculateEditedMacros = useCallback(function() {
    if (!meal || !editMode) return;
    
    // Check if any changes were made
    var hasChanges = false;
    for (var i = 0; i < ingredientAdjustments.length; i++) {
      var adj = ingredientAdjustments[i];
      if (adj.removed || 
          adj.displayQty !== adj.original_displayQty || 
          adj.displayUnitId !== adj.original_displayUnitId ||
          adj.grams !== adj.original_grams ||
          adj.is_new) {
        hasChanges = true;
        break;
      }
    }
    
    // If no changes, keep original macros
    if (!hasChanges) {
      setEditedMacros(null);
      setCalculatingMacros(false);
      return;
    }
    
    // Calculate total macros from all ingredients (same as admin meal editor)
    let totalCalories = 0;
    let totalProtein = 0;
    let totalCarbs = 0;
    let totalFat = 0;
    
    ingredientAdjustments.forEach(function(adj) {
      if (adj.removed) {
        // Removed ingredient: contributes 0
        return;
      }
      
      // Use per-gram nutrition if available (admin meals)
      const grams = adj.grams || 0;
      const perG = adj._perG || {};
      const hasPerG = (perG.cal || 0) > 0 || (perG.protein || 0) > 0;
      
      let cal, protein, carbs, fat;
      
      if (hasPerG) {
        // Admin meal path: calculate from grams × per-gram
        cal = grams * (perG.cal || 0);
        protein = grams * (perG.protein || 0);
        carbs = grams * (perG.carbs || 0);
        fat = grams * (perG.fat || 0);
      } else {
        // Custom meal path: use stored macros (updated by API on edit)
        var m = adj.macros || {};
        cal = m.calories || 0;
        protein = m.protein || 0;
        carbs = m.carbs || 0;
        fat = m.fat || 0;
      }
      
      // If cal is 0, compute from macros using 4/4/9 rule
      const effectiveCal = cal > 0 ? cal : (protein * 4) + (carbs * 4) + (fat * 9);
      
      totalCalories += effectiveCal;
      totalProtein += protein;
      totalCarbs += carbs;
      totalFat += fat;
    });
    
    // Set the calculated macros (ensure no negative values)
    setEditedMacros({
      meal_id: mealId,
      original_macros: {
        calories: meal.calories || 0,
        protein: meal.protein || 0,
        carbs: meal.carbs || 0,
        fat: meal.fat || 0
      },
      adjusted_macros: {
        calories: Math.max(0, Math.round(totalCalories)),
        protein: Math.max(0, Math.round(totalProtein)),
        carbs: Math.max(0, Math.round(totalCarbs)),
        fat: Math.max(0, Math.round(totalFat))
      },
      adjusted_ingredients: ingredientAdjustments.filter(function(a) { return !a.removed; }),
      is_edited: true
    });
    
    setCalculatingMacros(false);
  }, [meal, editMode, ingredientAdjustments, mealId]);

  // Debounced macro calculation - 600ms delay to avoid spam
  useEffect(function() {
    if (!editMode) return;
    
    var timeoutId = setTimeout(function() {
      calculateEditedMacros();
    }, 600);
    
    return function() {
      clearTimeout(timeoutId);
    };
  }, [editMode, ingredientAdjustments, calculateEditedMacros]);

  const isFavorite = favHas(mealId);
  const toggleFavorite = () => ctxToggleFavorite(mealId);

  // Build ingredient_details from ingredientAdjustments for storage
  function buildIngredientDetails(adjustments) {
    if (!adjustments || adjustments.length === 0) return null;
    var details = adjustments
      .filter(function(a) { return !a.removed; })
      .map(function(a) {
        return {
          name: a.name || '',
          food_id: a.food_id || (a.id && (a.id + '').startsWith('sp_ing_') ? a.id : null),
          quantity: a.displayQty || a.quantity || 1,
          unit: a.displayUnitId || a.unit || 'serving',
          grams: a.grams || 0,
          calories: a.macros ? (a.macros.calories || 0) : 0,
          protein: a.macros ? (a.macros.protein || 0) : 0,
          carbs: a.macros ? (a.macros.carbs || 0) : 0,
          fat: a.macros ? (a.macros.fat || 0) : 0
        };
      });
    return details.length > 0 ? details : null;
  }

  // Toggle edit mode
  const toggleEditMode = async function() {
    // Linked cook/leftover rows cannot be edited separately yet (v1).
    if (isLinked) {
      toast.info("Linked meals can't be edited separately yet.");
      return;
    }
    if (editMode) {
      // Exiting edit mode - check if we should save
      if (editedMacros && planId) {
        // Has edits AND has a planId - save to that plan entry
        await saveEditsToExistingPlan();
      } else if (editedMacros) {
        // Has edits but no planId - keep edits in memory, show toast
        toast.success('Edits preserved. Add to plan to save.');
      } else {
        // No edits - just reset
        resetEdits();
      }
    }
    setEditMode(!editMode);
  };

  // Save edited meal to an existing plan entry
  const saveEditsToExistingPlan = async function() {
    if (!planId || !editedMacros) return;
    
    setSavingEdits(true);
    try {
      // Format the ingredients for the API.
      // Persist enough state to fully restore the editor view on reopen
      // (displayQty/displayUnitId/_perG/portionOptions/id/food_id), in
      // addition to the canonical {name, quantity, unit, grams, macros}.
      const formattedIngredients = ingredientAdjustments
        .filter(function(adj) { return !adj.removed; })
        .map(function(adj) {
          return {
            id: adj.id || null,
            food_id: adj.food_id || null,
            name: adj.name,
            quantity: adj.displayQty,
            unit: adj.displayUnitId,
            displayQty: adj.displayQty,
            displayUnitId: adj.displayUnitId,
            grams: adj.grams,
            macros: adj.macros,
            _perG: adj._perG || null,
            portionOptions: adj.portionOptions || null,
            is_new: adj.is_new || false
          };
        });
      
      // Update the plan entry with custom macros and ingredients
      const response = await mealPlanAPI.updateMealPlanEdits(planId, {
        macros: editedMacros.adjusted_macros,
        custom_ingredients: formattedIngredients,
        is_edited: true
      });
      
      toast.success('Meal edits saved!');
      console.log('[MealView] Saved edits to plan:', response.data);
    } catch (err) {
      console.error('[MealView] Error saving edits:', err);
      toast.error('Failed to save edits');
    } finally {
      setSavingEdits(false);
    }
  };

  // Reset all edits - restore original data (same methodology as admin)
  // Must apply scaleFactor so reset returns to the SCALED view, not base
  const resetEdits = function() {
    if (meal && meal.ingredient_quantities) {
      // P3B: keep the allergen-omitted rows out of the reset (canonical) list too.
      var _omittedReset = new Set((planContext && planContext.omitted_ingredient_ids ? planContext.omitted_ingredient_ids : []).map(String));
      setIngredientAdjustments(meal.ingredient_quantities.filter(function(ing) {
        return !_omittedReset.has(String(ing.ingredient_id));
      }).map(function(ing) {
        var qty = ing.quantity;
        try {
          qty = parseFloat(ing.quantity) || 1;
        } catch (e) {
          qty = 1;
        }
        
        // Apply scale factor (same as initial load); cook card uses batch prep.
        var _ingScaleR = computeIngScale(meal.servings);
        var scaledQty = qty * _ingScaleR;
        var scaledGrams = (ing.grams || 0) * _ingScaleR;
        
        const perGramNutrition = {
          cal_per_g: ing.cal_per_g || 0,
          protein_per_g: ing.protein_per_g || 0,
          carbs_per_g: ing.carbs_per_g || 0,
          fat_per_g: ing.fat_per_g || 0
        };
        
        const macros = {
          calories: Math.round(scaledGrams * perGramNutrition.cal_per_g),
          protein: Math.round(scaledGrams * perGramNutrition.protein_per_g),
          carbs: Math.round(scaledGrams * perGramNutrition.carbs_per_g),
          fat: Math.round(scaledGrams * perGramNutrition.fat_per_g)
        };
        
        const portionOptions = ing.portion_options || [
          { id: 'g', label: 'grams', grams_per_unit: 1, is_estimate: false }
        ];
        
        return {
          id: ing.core_food_id || '',
          name: ing.name || '',
          displayQty: scaledQty,
          displayUnitId: ing.unit || 'g',
          grams: scaledGrams,
          original_displayQty: scaledQty,
          original_displayUnitId: ing.unit || 'g',
          original_grams: scaledGrams,
          _perG: {
            cal: perGramNutrition.cal_per_g,
            protein: perGramNutrition.protein_per_g,
            carbs: perGramNutrition.carbs_per_g,
            fat: perGramNutrition.fat_per_g
          },
          macros: macros,
          portionOptions: portionOptions,
          removed: false,
          isOptional: ing.is_optional || false
        };
      }));
    }
    setEditedMacros(null);
  };

  // Update ingredient quantity (same logic as admin meal editor)
  // For custom meals without _perG data, fetch from API
  const handleQtyChange = async function(index, qty) {
    const ingredient = ingredientAdjustments[index];
    const unitId = ingredient.displayUnitId || 'g';
    const perG = ingredient._perG || {};
    const hasPerG = perG.cal > 0 || perG.protein > 0 || perG.carbs > 0 || perG.fat > 0;
    
    if (hasPerG) {
      // Admin meal path: use local _perG math
      const option = (ingredient.portionOptions || []).find(function(o) { return o.id === unitId; });
      let newGrams = qty;
      if (option && option.grams_per_unit) {
        newGrams = qty * option.grams_per_unit;
      }
      const newMacros = {
        calories: Math.round(newGrams * (perG.cal || 0)),
        protein: Math.round(newGrams * (perG.protein || 0)),
        carbs: Math.round(newGrams * (perG.carbs || 0)),
        fat: Math.round(newGrams * (perG.fat || 0))
      };
      setIngredientAdjustments(function(prev) {
        var updated = prev.slice();
        updated[index] = Object.assign({}, updated[index], {
          displayQty: qty,
          grams: Math.round(newGrams * 10) / 10,
          macros: newMacros,
          removed: qty === 0
        });
        return updated;
      });
    } else if (ingredient.id && (ingredient.id + '').startsWith('sp_ing_') || ingredient.food_id) {
      // Custom meal path: fetch nutrition from API
      var foodId = ingredient.food_id || ingredient.id;
      try {
        var res = await logFoodAPI.getFoodNutrition(foodId, qty, unitId);
        var nutrition = res.data?.nutrition || {};
        setIngredientAdjustments(function(prev) {
          var updated = prev.slice();
          updated[index] = Object.assign({}, updated[index], {
            displayQty: qty,
            macros: {
              calories: Math.round(nutrition.calories || 0),
              protein: Math.round(nutrition.protein_g || 0),
              carbs: Math.round(nutrition.carbs_g || 0),
              fat: Math.round(nutrition.fat_g || 0)
            },
            removed: qty === 0
          });
          return updated;
        });
      } catch (err) {
        console.error('[MealView] Failed to fetch nutrition for qty change:', err);
        // Fallback: proportional scale from original
        var ratio = ingredient.original_displayQty ? (qty / ingredient.original_displayQty) : 1;
        var origMacros = ingredient.macros || {};
        setIngredientAdjustments(function(prev) {
          var updated = prev.slice();
          updated[index] = Object.assign({}, updated[index], {
            displayQty: qty,
            macros: {
              calories: Math.round((origMacros.calories || 0) * ratio),
              protein: Math.round((origMacros.protein || 0) * ratio),
              carbs: Math.round((origMacros.carbs || 0) * ratio),
              fat: Math.round((origMacros.fat || 0) * ratio)
            },
            removed: qty === 0
          });
          return updated;
        });
      }
    } else {
      // Text-only ingredient: just update qty
      setIngredientAdjustments(function(prev) {
        var updated = prev.slice();
        updated[index] = Object.assign({}, updated[index], {
          displayQty: qty,
          removed: qty === 0
        });
        return updated;
      });
    }
  };

  // Update ingredient unit (same logic as admin meal editor)
  // For custom meals without _perG data, fetch from API
  const handleUnitChange = async function(index, unitId) {
    const ingredient = ingredientAdjustments[index];
    const perG = ingredient._perG || {};
    const hasPerG = perG.cal > 0 || perG.protein > 0 || perG.carbs > 0 || perG.fat > 0;
    
    if (hasPerG) {
      // Admin meal path: use local _perG math
      const option = (ingredient.portionOptions || []).find(function(o) { return o.id === unitId; });
      if (option && ingredient.displayQty) {
        const newGrams = ingredient.displayQty * option.grams_per_unit;
        const newMacros = {
          calories: Math.round(newGrams * (perG.cal || 0)),
          protein: Math.round(newGrams * (perG.protein || 0)),
          carbs: Math.round(newGrams * (perG.carbs || 0)),
          fat: Math.round(newGrams * (perG.fat || 0))
        };
        setIngredientAdjustments(function(prev) {
          var updated = prev.slice();
          updated[index] = Object.assign({}, updated[index], {
            displayUnitId: unitId,
            grams: Math.round(newGrams * 10) / 10,
            macros: newMacros
          });
          return updated;
        });
      } else {
        setIngredientAdjustments(function(prev) {
          var updated = prev.slice();
          updated[index] = Object.assign({}, updated[index], { displayUnitId: unitId });
          return updated;
        });
      }
    } else if (ingredient.id && (ingredient.id + '').startsWith('sp_ing_') || ingredient.food_id) {
      // Custom meal path: fetch nutrition from API
      var foodId = ingredient.food_id || ingredient.id;
      var qty = ingredient.displayQty || 1;
      try {
        var res = await logFoodAPI.getFoodNutrition(foodId, qty, unitId);
        var nutrition = res.data?.nutrition || {};
        var options = res.data?.serving_options || [];
        // Also hydrate portionOptions from this response
        var newPortionOptions = options.map(function(opt) {
          return { id: opt.unit, label: opt.unit, grams_per_unit: 1, is_estimate: false };
        });
        
        setIngredientAdjustments(function(prev) {
          var updated = prev.slice();
          updated[index] = Object.assign({}, updated[index], {
            displayUnitId: unitId,
            macros: {
              calories: Math.round(nutrition.calories || 0),
              protein: Math.round(nutrition.protein_g || 0),
              carbs: Math.round(nutrition.carbs_g || 0),
              fat: Math.round(nutrition.fat_g || 0)
            },
            portionOptions: newPortionOptions.length > 0 ? newPortionOptions : updated[index].portionOptions
          });
          return updated;
        });
      } catch (err) {
        console.error('[MealView] Failed to fetch nutrition for unit change:', err);
        setIngredientAdjustments(function(prev) {
          var updated = prev.slice();
          updated[index] = Object.assign({}, updated[index], { displayUnitId: unitId });
          return updated;
        });
      }
    } else {
      setIngredientAdjustments(function(prev) {
        var updated = prev.slice();
        updated[index] = Object.assign({}, updated[index], { displayUnitId: unitId });
        return updated;
      });
    }
    setShowUnitPicker(null);
  };

  // Remove ingredient completely
  const removeIngredient = function(index) {
    setIngredientAdjustments(function(prev) {
      var updated = prev.slice();
      updated[index] = Object.assign({}, updated[index], { removed: true });
      return updated;
    });
  };

  // Search for ingredients to add (uses same core foods search as admin)
  const searchIngredients = async function(query) {
    if (!query || query.length < 2) {
      setIngredientSearchResults([]);
      return;
    }
    
    setSearchingIngredients(true);
    try {
      // Use the SAME endpoint as admin meal editor
      const response = await mealAPI.searchCoreFoods(query, 15);
      if (response.data && Array.isArray(response.data)) {
        setIngredientSearchResults(response.data);
      } else {
        setIngredientSearchResults([]);
      }
    } catch (err) {
      console.error('Error searching ingredients:', err);
      setIngredientSearchResults([]);
      toast.error('Failed to search ingredients');
    } finally {
      setSearchingIngredients(false);
    }
  };

  // Debounced ingredient search
  useEffect(function() {
    if (!showAddIngredient) return;
    
    const timer = setTimeout(function() {
      searchIngredients(ingredientSearchQuery);
    }, 300);
    
    return function() { clearTimeout(timer); };
  }, [ingredientSearchQuery, showAddIngredient]);

  // Add ingredient from search results (same data structure as admin)
  const addIngredientFromSearch = function(food) {
    // Get portion options (same format as admin)
    const portionOptions = food.portion_options || [
      { id: 'g', label: 'grams', grams_per_unit: 1, is_estimate: false }
    ];
    
    // Default to first unit option
    const defaultUnit = portionOptions[0] || { id: 'g', grams_per_unit: 1 };
    const defaultQty = 100; // Start with 100g equivalent
    const defaultGrams = defaultQty * (defaultUnit.grams_per_unit || 1);
    
    // Get per-gram nutrition
    const perG = {
      cal: food.cal_per_g || 0,
      protein: food.protein_per_g || 0,
      carbs: food.carbs_per_g || 0,
      fat: food.fat_per_g || 0
    };
    
    // Calculate initial macros
    const macros = {
      calories: Math.round(defaultGrams * perG.cal),
      protein: Math.round(defaultGrams * perG.protein),
      carbs: Math.round(defaultGrams * perG.carbs),
      fat: Math.round(defaultGrams * perG.fat)
    };
    
    // Create new ingredient with same structure as existing ingredients
    const newIngredient = {
      id: food.food_id || '',
      name: food.name,
      displayQty: defaultQty,
      displayUnitId: defaultUnit.id,
      grams: defaultGrams,
      original_displayQty: defaultQty,
      original_displayUnitId: defaultUnit.id,
      original_grams: defaultGrams,
      _perG: perG,
      macros: macros,
      portionOptions: portionOptions,
      removed: false,
      is_new: true
    };
    
    setIngredientAdjustments(function(prev) {
      return prev.concat([newIngredient]);
    });
    
    // Close search UI
    setShowAddIngredient(false);
    setIngredientSearchQuery('');
    setIngredientSearchResults([]);
    
    toast.success('Added ' + food.name);
  };

  // Check if selected slot already has a meal
  const checkExistingMeal = async (date, slot) => {
    try {
      const response = await mealPlanAPI.getDailyPlan(date);
      const plans = response.data || [];
      const existing = plans.find(p => p.meal_type === slot && p.status !== 'skipped');
      return existing || null;
    } catch (err) {
      console.error('[MealView] Error checking existing meal:', err);
      return null;
    }
  };

  // Handle date selection in step 1
  const handleDateSelect = (dates) => {
    if (dates && dates.length > 0) {
      setPlanDate(dates[0]);
    }
  };

  // Move to step 2 after date selection
  const handleDateConfirm = () => {
    setAddToPlanStep(2);
  };

  // Handle slot selection in step 2
  const handleSlotSelect = async (slot) => {
    setSelectedMealSlot(slot);
    
    // Check if slot already has a meal
    const existing = await checkExistingMeal(planDate, slot);
    if (existing) {
      setExistingMealInSlot(existing);
      setShowReplaceConfirm(true);
    } else {
      // No conflict, proceed to add
      setExistingMealInSlot(null);
      await executeAddToPlan(slot);
    }
  };

  // Execute the actual add to plan
  const executeAddToPlan = async (slot) => {
    if (!meal) return;
    
    const targetSlot = slot || selectedMealSlot;
    
    console.log('[MealView] Adding to plan:', {
      date: planDate,
      meal_type: targetSlot,
      meal_id: meal.meal_id,
      meal_name: meal.name,
      is_edited: editMode && editedMacros
    });
    
    setAddingToPlan(true);
    try {
      // NO portion_multiplier - meals are fixed objects
      var planData = {
        date: planDate,
        meal_type: targetSlot,
        meal_id: meal.meal_id,
        meal_name: meal.name,
        source: guidedMode ? 'guided' : 'manual',
        is_edited: editMode && editedMacros != null
      };
      
      // Always include macros so the plan entry is self-contained
      // (prevents 0-calorie display when backend can't look up custom/logged foods)
      if (editMode && editedMacros) {
        planData.macros = editedMacros.adjusted_macros;
        planData.custom_ingredients = editedMacros.adjusted_ingredients;
        planData.ingredient_details = buildIngredientDetails(editedMacros.adjusted_ingredients);
      } else {
        planData.macros = {
          calories: currentMacros.calories || 0,
          protein: currentMacros.protein || 0,
          carbs: currentMacros.carbs || 0,
          fat: currentMacros.fat || 0
        };
        planData.ingredient_details = buildIngredientDetails(ingredientAdjustments);
      }
      
      const response = await mealPlanAPI.addMealPlan(planData);
      
      console.log('[MealView] Add to plan response:', response.data);
      toast.success(editMode && editedMacros ? 'Added edited meal to your plan!' : 'Added to your plan!');
      closeAddToPlanModal();
      
      // Navigate based on context
      leaveAfterAdd(guidedMode ? '/plan/guided?date=' + planDate : '/home');
    } catch (err) {
      console.error('[MealView] Error adding to plan:', err);
      const errorDetail = err.response?.data?.detail || err.message || 'Unknown error';
      console.error('[MealView] Error detail:', errorDetail);
      toast.error('Failed to add: ' + errorDetail);
    } finally {
      setAddingToPlan(false);
    }
  };

  // Handle replacing existing meal
  const handleReplaceMeal = async () => {
    if (existingMealInSlot && existingMealInSlot.plan_id) {
      try {
        await mealPlanAPI.deleteMealPlan(existingMealInSlot.plan_id);
        console.log('[MealView] Deleted existing meal:', existingMealInSlot.meal_name);
      } catch (err) {
        console.error('[MealView] Error deleting existing meal:', err);
      }
    }
    setShowReplaceConfirm(false);
    setExistingMealInSlot(null);
    await executeAddToPlan(selectedMealSlot);
  };

  // Cancel replacement - close confirmation
  const cancelReplace = () => {
    setShowReplaceConfirm(false);
    setExistingMealInSlot(null);
  };

  // Open add to plan modal
  const openAddToPlanModal = () => {
    setPlanDate(contextDate || getTodayString());
    setSelectedMealSlot('');
    setAddToPlanStep(1);
    setShowAddToPlan(true);
  };

  // Close add to plan modal
  const closeAddToPlanModal = () => {
    setShowAddToPlan(false);
    // Dismissing leaves no planning date behind (it never affected Log anyway).
    setPlanDate(contextDate || getTodayString());
    setAddToPlanStep(1);
    setShowReplaceConfirm(false);
    setExistingMealInSlot(null);
  };

  // Legacy handler for direct add (keep for backward compatibility)
  const handleAddToPlan = async () => {
    if (!selectedMealSlot) {
      toast.error('Please select a meal slot');
      return;
    }
    await executeAddToPlan(selectedMealSlot);
  };

  // Log meal directly (for when coming from Home page or re-logging from Progress)
  const handleLogMeal = async function() {
    if (!meal) return;
    
    // The ONE logging date the CTA shows (see logDate above). The Add to Plan
    // modal's planDate is never consulted here.
    var targetDate = logDate;

    console.log('[MealView] Logging meal:', {
      date: targetDate,
      meal_type: contextMealType || meal.category || 'Snack',
      meal_id: meal.meal_id,
      meal_name: meal.name,
      is_edited: editMode && editedMacros,
      planId: planId,
      isPlannedRow: isPlannedRow,
      isAlreadyLogged: isAlreadyLogged
    });

    setAddingToPlan(true);
    try {
      if (isPlannedRow) {
        // Pending/planned saved row, logged in its OWN saved date context
        // (targetDate === rowDate) — complete that existing row.
        await mealPlanAPI.updateMealPlan(planId, 'completed');
        
        // If user edited macros, also save the edits
        if (editMode && editedMacros) {
          await mealPlanAPI.updateMealPlanEdits(planId, {
            macros: editedMacros.adjusted_macros,
            custom_ingredients: editedMacros.adjusted_ingredients,
            is_edited: true
          });
        }
        
        toast.success(editMode && editedMacros ? 'Logged edited meal!' : 'Meal logged!');
      } else {
        // Either no planId (fresh log from browsing) OR already logged ("Log Again")
        // ALWAYS create a brand new plan entry
        var planData = {
          date: targetDate,
          meal_type: contextMealType || meal.category || 'Snack',
          meal_id: meal.meal_id,
          meal_name: meal.name,
          source: 'manual',
          status: 'completed',
          is_edited: editMode && editedMacros != null
        };
        
        if (editMode && editedMacros) {
          planData.macros = editedMacros.adjusted_macros;
          planData.custom_ingredients = editedMacros.adjusted_ingredients;
          planData.ingredient_details = buildIngredientDetails(editedMacros.adjusted_ingredients);
        } else {
          // Even without edits, include macros and ingredient_details
          planData.macros = {
            calories: currentMacros.calories || 0,
            protein: currentMacros.protein || 0,
            carbs: currentMacros.carbs || 0,
            fat: currentMacros.fat || 0
          };
          planData.ingredient_details = buildIngredientDetails(ingredientAdjustments);
        }
        
        await mealPlanAPI.addMealPlan(planData);
        toast.success(isAlreadyLogged ? 'Logged again!' : (editMode && editedMacros ? 'Logged edited meal!' : 'Meal logged!'));
      }
      
      // Navigate back — prefer return param, then home
      var returnPath = safeReturnPath(searchParams.get('return'), '/home');
      leaveAfterAdd(returnPath);
    } catch (err) {
      console.error('[MealView] Error logging meal:', err);
      toast.error('Failed to log meal');
    } finally {
      setAddingToPlan(false);
    }
  };
  
  // Choose an unsaved Guided candidate for the exact Step slot. Candidate
  // details are read-only, so only recipe identity is submitted.
  const quickAddToPlan = async () => {
    if (!meal || !guidedCandidateMode || !contextMealType) return;
    setAddingToPlan(true);
    try {
      if (stepBrowseCandidate && contextMealType === 'Snack') {
        const day = await mealPlanAPI.getDailyPlan(contextDate);
        if (pendingSnackMealIds(day && day.data)[meal.meal_id]) {
          toast.error(meal.name + ' is already added as a Snack');
          return;
        }
      }
      await mealPlanAPI.addMealPlan({
        date: contextDate,
        meal_type: contextMealType,
        meal_id: meal.meal_id,
        meal_name: meal.name,
        source: 'guided'
      });
      toast.success(meal.name + ' added!');
      finishFlow(navigate, location, safeReturnPath(searchParams.get('return'), '/plan/guided?date=' + contextDate));
    } catch (err) {
      console.error('[MealView] Guided candidate add error:', err);
      toast.error('Failed to add meal');
    } finally {
      setAddingToPlan(false);
    }
  };

  // Swap context: replace the outgoing row with this meal using the SAME
  // write as the Swap browser (atomic child-only replace for a linked
  // leftover; otherwise delete + add with source 'swap'), then finish the
  // whole Swap back where it started.
  const swapToThisMeal = async () => {
    if (!meal || !swapCtx) return;
    setAddingToPlan(true);
    try {
      await performSwap(swapCtx, {
        date: contextDate,
        mealType: contextMealType || meal.category,
        meal: { meal_id: meal.meal_id, name: meal.name },
      });
      toast.success('Swapped to ' + meal.name);
      finishFlow(navigate, location, swapFlowReturn);
    } catch (err) {
      console.error('[MealView] Swap error:', err);
      toast.error(swapErrorMessage(err, swapCtx));
    } finally {
      setAddingToPlan(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Generate date options for the next 7 days
  const dateOptions = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    const dateStr = formatDateString(date);
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    dateOptions.push({ value: dateStr, label: label });
  }

  const mealSlots = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4" data-testid="meal-view-loading">
        <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        <p className="text-muted-foreground">Loading meal details...</p>
      </div>
    );
  }

  // Canonical-context failure for a LINKED row: never render possibly-wrong
  // one-portion preparation quantities — offer a safe retry instead.
  if (contextError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-6" data-testid="meal-view-context-error">
        <div className="text-center space-y-4 max-w-sm">
          <p className="text-muted-foreground">
            Couldn't load the correct preparation quantities for this linked meal. To avoid showing the wrong amounts, we didn't display the recipe.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={function() { setReloadKey(function(k) { return k + 1; }); }}
              className="rounded-full"
              data-testid="context-retry-btn"
            >
              Retry
            </Button>
            <Button
              onClick={function() { if (window.history.length > 1) { navigate(-1); } else { navigate('/meals'); } }}
              variant="outline"
              className="rounded-full"
              data-testid="context-back-btn"
            >
              <ArrowLeft size={18} className="mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !meal) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-6" data-testid="meal-view-error">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">{error || 'Meal not found'}</p>
          <Button
            onClick={function() { 
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/meals');
              }
            }}
            variant="outline"
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Process data before rendering
  const imageUrl = imageError || !meal.image_url ? PLACEHOLDER_IMAGE : meal.image_url;
  const hasInstructions = meal.cooking_instructions && meal.cooking_instructions.length > 0;
  const hasIngredients = meal.ingredient_quantities && meal.ingredient_quantities.length > 0;
  const hasAllergens = meal.allergens && meal.allergens.length > 0;

  // Pre-process arrays for safe rendering
  const instructionsList = hasInstructions ? meal.cooking_instructions : [];
  const allergensList = hasAllergens ? meal.allergens : [];

  // Get current macros (edited, or from meal — already scaled if scaleFactor applied)
  const currentMacros = editedMacros ? editedMacros.adjusted_macros : {
    calories: meal.calories,
    protein: meal.protein,
    carbs: meal.carbs,
    fat: meal.fat
  };

  return (
    <div className="min-h-screen pb-bottom-nav" data-testid="meal-view-page">
      {/* CSS to hide number input spinners */}
      <style>{hideSpinnerStyles}</style>
      
      {/* Hero Image Section */}
      <div className="relative h-72 bg-secondary overflow-hidden">
        <MealImage 
          src={imageUrl} 
          fallback={PLACEHOLDER_IMAGE}
          alt={meal.name}
          onError={handleImageError}
          className="w-full h-full object-cover"
          data-testid="meal-image"
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
        
        {/* Back button */}
        <Button
          onClick={function() { 
            // If there's history, go back; otherwise go to meals or home
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/meals');
            }
          }}
          variant="ghost"
          size="icon"
          className="absolute top-safe left-4 rounded-full bg-background/80 backdrop-blur hover:bg-background/90"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        
        {/* Favorite button */}
        <Button
          onClick={toggleFavorite}
          variant="ghost"
          size="icon"
          className="absolute top-safe right-4 rounded-full bg-background/80 backdrop-blur hover:bg-background/90"
          data-testid="favorite-btn"
        >
          <Heart 
            size={20} 
            className={isFavorite ? 'fill-red-500 text-red-500' : 'text-foreground'} 
          />
        </Button>
        
        {/* Edit mode badge */}
        {editMode && (
          <span className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-yellow-500 text-white text-sm font-medium flex items-center gap-1">
            <Edit3 size={14} />
            Editing
          </span>
        )}
        
        {/* Meal type badge */}
        {contextMealType && !editMode && (
          <span className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-accent text-white text-sm font-medium">
            {contextMealType}
          </span>
        )}
      </div>

      <div className="p-6 space-y-6">
        {/* Title & Meta */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-heading font-bold text-2xl mb-2" data-testid="meal-name">{meal.name}</h1>

              {isCookMeal && (
                <div className="mb-3 p-3 rounded-xl bg-accent/10 border border-accent/20" data-testid="cook-batch-banner">
                  <p className="text-sm font-semibold text-accent">Cook 2 portions</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Eat one tonight, save one for tomorrow. Ingredients are for both portions; macros are for tonight's portion.
                  </p>
                </div>
              )}
              {isLeftoverMeal && (
                <div className="mb-3 p-3 rounded-xl bg-secondary/60 border border-border/40" data-testid="leftover-reheat-banner">
                  <p className="text-sm font-semibold">Leftover from yesterday</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Reheat until warmed through. Macros are for today's portion.</p>
                  {!editMode && !showLeftoverRecipe && (hasIngredients || hasInstructions) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={function() { setShowLeftoverRecipe(true); }}
                      data-testid="view-original-recipe-btn"
                    >
                      View original recipe
                    </Button>
                  )}
                </div>
              )}
              
              {meal.category && !isLeftoverMeal && (
                <span className="inline-block px-3 py-1 rounded-full bg-secondary text-sm text-muted-foreground mb-3">
                  {meal.category}
                </span>
              )}
              
              {isScaled && (
                <span 
                  className="inline-block ml-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-sm font-medium mb-3"
                  data-testid="scale-indicator"
                >
                  {scaleLabel || (Math.round(scaleFactor * 100) + '% portion')}
                </span>
              )}
            </div>
            
            {/* Edit toggle button — only show for meals with actual ingredients to edit */}
            {/* Check ingredient_quantities (structured meals) OR ingredients (custom meals) */}
            {((meal.ingredient_quantities && meal.ingredient_quantities.length > 0) || (meal.ingredients && meal.ingredients.length > 0)) && (
            // A Swap writes the recipe as-is (what the Swap browser judged), so
            // editing is off during a Swap, as it is for linked leftover rows.
            (isLinked || swapCtx || guidedCandidateMode) ? (
              <Button
                variant="outline"
                size="sm"
                disabled
                className="opacity-60 cursor-not-allowed"
                data-testid="edit-toggle-btn-disabled"
              >
                <Edit3 size={16} className="mr-1" />
                Edit
              </Button>
            ) : (
            <Button
              onClick={toggleEditMode}
              variant={editMode ? 'default' : 'outline'}
              size="sm"
              className={editMode ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}
              disabled={savingEdits}
              data-testid="edit-toggle-btn"
            >
              {savingEdits ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                  Saving...
                </>
              ) : editMode ? (
                <>
                  {editedMacros && planId ? (
                    <>
                      <Check size={16} className="mr-1" />
                      Save
                    </>
                  ) : (
                    <>
                      <Check size={16} className="mr-1" />
                      Done
                    </>
                  )}
                </>
              ) : (
                <>
                  <Edit3 size={16} className="mr-1" />
                  Edit
                </>
              )}
            </Button>
            )
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {meal.cook_time_minutes > 0 && (
              <div className="flex items-center gap-1">
                <Clock size={16} className="text-accent" />
                <span>{meal.cook_time_minutes} min</span>
              </div>
            )}
            {meal.serving_size > 0 && (
              <div className="flex items-center gap-1">
                <Users size={16} className="text-accent" />
                <span>{meal.serving_size} {meal.serving_size === 1 ? 'serving' : 'servings'}</span>
              </div>
            )}
          </div>

          {isLinked && !editMode && (
            <p className="text-xs text-muted-foreground mt-3" data-testid="linked-edit-note">
              This meal is part of a cook-once, eat-twice pair. Linked meals can't be edited separately yet.
            </p>
          )}
        </motion.div>

        {/* Edit Mode Notice - NO portion scaling, only ingredient editing */}
        {editMode && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4"
            data-testid="edit-mode-notice"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">Edit Mode</p>
                <p className="text-xs text-muted-foreground">
                  {planId 
                    ? 'Editing your logged meal - changes will be saved when you click Save'
                    : 'Adjust individual ingredients below. Add to plan to save.'
                  }
                </p>
              </div>
            </div>
            
            {/* Reset button */}
            {editedMacros && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetEdits}
                className="mt-3 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-500/10"
                data-testid="reset-edits-btn"
              >
                <RotateCcw size={14} className="mr-1" />
                Reset All Changes
              </Button>
            )}
          </motion.div>
        )}

        {/* Macro Summary */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`grid grid-cols-4 gap-3 border rounded-xl p-4 ${editMode ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-card border-border/40'}`}
          data-testid="macro-summary"
        >
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Calories</p>
            <p className="font-heading font-bold text-lg text-accent">
              {calculatingMacros ? '...' : Math.round(currentMacros.calories)}
            </p>
            {editMode && editedMacros && (
              <p className="text-[10px] text-muted-foreground line-through">{Math.round(meal.calories)}</p>
            )}
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Protein</p>
            <p className="font-heading font-bold text-lg">
              {calculatingMacros ? '...' : formatMacro(currentMacros.protein)}g
            </p>
            {editMode && editedMacros && (
              <p className="text-[10px] text-muted-foreground line-through">{Math.round(meal.protein)}g</p>
            )}
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Carbs</p>
            <p className="font-heading font-bold text-lg">
              {calculatingMacros ? '...' : formatMacro(currentMacros.carbs)}g
            </p>
            {editMode && editedMacros && (
              <p className="text-[10px] text-muted-foreground line-through">{Math.round(meal.carbs)}g</p>
            )}
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Fat</p>
            <p className="font-heading font-bold text-lg">
              {calculatingMacros ? '...' : formatMacro(currentMacros.fat)}g
            </p>
            {editMode && editedMacros && (
              <p className="text-[10px] text-muted-foreground line-through">{Math.round(meal.fat)}g</p>
            )}
          </div>
        </motion.div>

        {/* Ingredients (Editable in edit mode) */}
        {(hasIngredients || editMode) && !(isLeftoverMeal && !editMode && !showLeftoverRecipe) && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`border rounded-xl p-6 ${editMode ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-card border-border/40'}`}
            data-testid="ingredients-section"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg">
                Ingredients
                {editMode && <span className="text-sm font-normal text-muted-foreground ml-2">(tap to edit)</span>}
              </h2>
              {editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={function() { setShowAddIngredient(true); }}
                  className="text-accent border-accent/30 hover:bg-accent/10"
                  data-testid="add-ingredient-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {ingredientAdjustments.map(function(adj, idx) {
                // Use admin-style data structure
                const displayQty = adj.displayQty || 0;
                const displayUnit = adj.displayUnitId || 'g';
                const isRemoved = adj.removed;
                const isModified = editMode && (
                  adj.displayQty !== adj.original_displayQty || 
                  adj.displayUnitId !== adj.original_displayUnitId || 
                  isRemoved || 
                  adj.is_new
                );
                
                // Get portion options (same format as admin meal editor)
                const portionOptions = adj.portionOptions || [];
                const hasPortionOptions = portionOptions.length > 0;
                
                return (
                  <div 
                    key={'ingredient-' + idx} 
                    className={`flex items-center gap-2 ${isRemoved ? 'opacity-40' : ''}`}
                  >
                    {editMode ? (
                      <>
                        {/* Quantity input - same as admin meal editor */}
                        <input
                          type="number"
                          value={displayQty}
                          onChange={function(e) { 
                            const val = parseFloat(e.target.value) || 0;
                            handleQtyChange(idx, val); 
                          }}
                          className="w-16 text-center bg-white/80 border border-border/60 rounded px-2 py-1 focus:border-accent focus:ring-1 focus:ring-accent/20 outline-none font-mono text-sm appearance-none"
                          style={{ MozAppearance: 'textfield' }}
                          step="any"
                          min="0"
                          data-testid={'ingredient-input-' + idx}
                        />
                        
                        {/* Unit selector dropdown - uses portionOptions from USDA (same as admin) */}
                        <div className="relative">
                          <button
                            onClick={function() { 
                              var nextIdx = showUnitPicker === idx ? null : idx;
                              setShowUnitPicker(nextIdx);
                              if (nextIdx !== null) hydratePortionOptions(idx);
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-white/80 border border-border/60 rounded hover:border-accent min-w-[70px] justify-between"
                            data-testid={'ingredient-unit-' + idx}
                          >
                            <span>{displayUnit}</span>
                            <ChevronDown size={12} />
                          </button>
                          {showUnitPicker === idx && hasPortionOptions && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-border rounded-lg shadow-lg z-20 max-h-52 overflow-y-auto min-w-[140px]">
                              {loadingUnitOptions === idx ? (
                                <div className="p-3 text-center">
                                  <span className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin inline-block"></span>
                                </div>
                              ) : (
                                portionOptions.map(function(opt) {
                                  return (
                                    <button
                                      key={opt.id}
                                      onClick={function() { handleUnitChange(idx, opt.id); }}
                                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-accent/10 ${displayUnit === opt.id ? 'text-accent font-medium bg-accent/5' : ''}`}
                                    >
                                      <span>{opt.label || opt.id}</span>
                                      {opt.is_estimate && <span className="ml-1 text-[10px] text-muted-foreground">(est)</span>}
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Ingredient name with modification indicator */}
                        <span className={`flex-1 text-sm ${isModified ? 'text-yellow-600 font-medium' : 'text-foreground'} ${isRemoved ? 'line-through' : ''}`}>
                          {adj.name}
                          {adj.is_new && <span className="ml-1 text-xs text-accent">(new)</span>}
                        </span>
                        
                        {/* Ingredient macros (same as admin shows) */}
                        {adj.macros && !isRemoved && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatMacro(adj.macros.calories)}cal
                          </span>
                        )}
                        
                        {/* Remove button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={function() { removeIngredient(idx); }}
                          className="h-7 w-7 rounded-full hover:bg-red-500/10 text-red-400 hover:text-red-500"
                          data-testid={'ingredient-remove-' + idx}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="text-accent">•</span>
                        <span className="text-foreground">{stripEmbeddedMeasure(adj.name, displayUnit)}</span>
                        <span className="text-gray-400 text-sm ml-1">
                          {toFraction(displayQty)} {displayUnit}
                        </span>
                        {adj.prep_note ? (
                          <span className="text-gray-500 text-xs ml-1 italic">
                            ({adj.prep_note})
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Add Ingredient Search Modal */}
            {showAddIngredient && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" style={kb ? { bottom: kb } : undefined} data-testid="add-ingredient-sheet">
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  className="bg-background rounded-t-2xl w-full max-w-lg max-h-[70vh] overflow-hidden"
                  // Keyboard open: fit the space above it (search pinned, results take the rest and scroll).
                  style={kb ? { maxHeight: 'calc(100% - env(safe-area-inset-top) - 0.5rem)', display: 'flex', flexDirection: 'column' } : undefined}
                  data-testid="add-ingredient-panel"
                >
                  <div className="p-4 border-b border-border shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">Add Ingredient</h3>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={function() { 
                          setShowAddIngredient(false);
                          setIngredientSearchQuery('');
                          setIngredientSearchResults([]);
                        }}
                      >
                        <X size={20} />
                      </Button>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <input
                        type="text"
                        value={ingredientSearchQuery}
                        onChange={function(e) { setIngredientSearchQuery(e.target.value); }}
                        placeholder="Search ingredients..."
                        className="w-full pl-10 pr-4 py-3 bg-secondary rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent/20"
                        autoFocus
                        data-testid="add-ingredient-search-input"
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[50vh] p-2" style={kb ? { maxHeight: 'none', flex: '1 1 auto', minHeight: 0 } : undefined} data-testid="add-ingredient-results">
                    {searchingIngredients ? (
                      <div className="flex justify-center py-8">
                        <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : ingredientSearchResults.length > 0 ? (
                      <div className="space-y-1">
                        {ingredientSearchResults.map(function(food) {
                          // Use admin-style data format (macros_per_100g)
                          const m = food.macros_per_100g || {};
                          return (
                            <button
                              key={food.food_id || food.name}
                              onClick={function() { addIngredientFromSearch(food); }}
                              className="w-full text-left p-3 rounded-lg hover:bg-accent/10 transition-colors"
                            >
                              <p className="font-medium text-sm">{food.name}</p>
                              <p className="text-xs text-muted-foreground">
                                per 100g: {formatMacro(m.calories)} cal • {formatMacro(m.protein)}g P • {formatMacro(m.carbs)}g C • {formatMacro(m.fat)}g F
                              </p>
                              {food.source === 'usda' && (
                                <span className="text-[10px] text-accent">USDA</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : ingredientSearchQuery.length >= 2 ? (
                      <p className="text-center text-muted-foreground py-8">No ingredients found</p>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">Type at least 2 characters to search</p>
                    )}
                  </div>
                </motion.div>
              </div>
            )}
          </motion.div>
        )}

        {/* Cooking Instructions — collapsed together with ingredients on a
            leftover row until "View original recipe" is tapped. */}
        {hasInstructions && !(isLeftoverMeal && !editMode && !showLeftoverRecipe) && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-border/40 rounded-xl p-6"
            data-testid="instructions-section"
          >
            <h2 className="font-heading font-semibold text-lg mb-4">Cooking Instructions</h2>
            <div className="space-y-4">
              {instructionsList.map(function(step, idx) {
                const stepNumber = idx + 1;
                const stepText = step || '';
                
                return (
                  <div key={'step-' + idx} className="flex gap-4">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-accent/10 text-accent flex items-center justify-center text-sm font-semibold">
                      {stepNumber}
                    </span>
                    <p className="flex-1 pt-0.5 text-foreground leading-relaxed">{stepText}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Allergen Information */}
        {hasAllergens && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card border border-border/40 rounded-xl p-6"
            data-testid="allergens-section"
          >
            <h2 className="font-heading font-semibold text-lg mb-4">Allergen Information</h2>
            <div className="flex flex-wrap gap-2">
              {allergensList.map(function(allergen, idx) {
                const displayName = allergen ? allergen.replace(/_/g, ' ') : '';
                
                return (
                  <span
                    key={'allergen-' + idx}
                    className="px-3 py-1 rounded-full bg-destructive/10 text-destructive text-sm capitalize"
                  >
                    {displayName}
                  </span>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-3"
        >
          {swapCtx ? (
            // Swap context: complete the Swap from here (never "Add as").
            <Button
              onClick={swapToThisMeal}
              disabled={addingToPlan || !meal}
              className="w-full h-14 rounded-full bg-accent text-white font-semibold text-lg hover:bg-accent/90"
              data-testid="swap-to-this-btn"
            >
              {addingToPlan ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Swapping...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <RefreshCw size={20} />
                  Swap to this meal
                </span>
              )}
            </Button>
          ) : guidedCandidateMode && contextMealType ? (
            // Quick add button for guided mode
            <Button
              onClick={quickAddToPlan}
              disabled={addingToPlan}
              className="w-full h-14 rounded-full bg-accent text-white font-semibold text-lg hover:bg-accent/90"
              data-testid="quick-add-btn"
            >
              {addingToPlan ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Adding...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check size={20} />
                  Choose for {contextMealType}
                </span>
              )}
            </Button>
          ) : (
            <>
              {/* Already-logged indicator */}
              {isAlreadyLogged && (
                <p className="text-center text-sm text-muted-foreground" data-testid="already-logged-label">
                  Already logged on {rowDateLabel}
                </p>
              )}
              
              {/* Log Now / Log Again button — show if we have mealType context OR this is a re-log */}
              {(contextMealType || isAlreadyLogged) && (
                <Button
                  onClick={handleLogMeal}
                  disabled={addingToPlan}
                  className="w-full h-14 rounded-full bg-accent text-white font-semibold text-lg hover:bg-accent/90"
                  data-testid={isAlreadyLogged ? 'log-again-btn' : 'log-meal-btn'}
                >
                  {addingToPlan ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Logging...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {isAlreadyLogged ? <RotateCcw size={20} /> : <Check size={20} />}
                      {isAlreadyLogged 
                        ? ('Log Again' + (editMode && editedMacros ? ' (Edited)' : ''))
                        : ('Log on ' + logDateLabel + (editMode && editedMacros ? ' (Edited)' : ''))
                      }
                    </span>
                  )}
                </Button>
              )}
              
              {/* Standard add with modal */}
              <Button
                onClick={openAddToPlanModal}
                variant={contextMealType || isAlreadyLogged ? 'outline' : 'default'}
                className={`w-full h-14 rounded-full font-semibold text-lg ${!(contextMealType || isAlreadyLogged) ? 'bg-accent text-white hover:bg-accent/90' : ''}`}
                data-testid="add-to-plan-btn"
              >
                <Plus size={20} className="mr-2" />
                {contextMealType || isAlreadyLogged ? 'Add to Different Day' : 'Add to Plan'}
                {editMode && editedMacros && !(contextMealType || isAlreadyLogged) && ' (Edited)'}
              </Button>
            </>
          )}
        </motion.div>
      </div>

      {/* Add to Plan Modal */}
      <AnimatePresence>
        {showAddToPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-center"
            onClick={function() { setShowAddToPlan(false); }}
          >
            <motion.div
              initial={{ y: 300 }}
              animate={{ y: 0 }}
              exit={{ y: 300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-background rounded-t-3xl w-full max-w-lg p-6 pb-10"
              onClick={function(e) { e.stopPropagation(); }}
              data-testid="add-to-plan-modal"
            >
              {/* Modal header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {addToPlanStep === 2 && (
                    <button
                      onClick={function() { setAddToPlanStep(1); }}
                      className="p-1 rounded-full hover:bg-secondary"
                    >
                      <ArrowLeft size={18} />
                    </button>
                  )}
                  <h3 className="font-heading font-bold text-xl">
                    {addToPlanStep === 1 ? 'Add meal to plan' : 'Select meal slot'}
                    {editMode && editedMacros && (
                      <span className="ml-2 text-sm font-normal text-yellow-600">(Edited)</span>
                    )}
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeAddToPlanModal}
                  className="rounded-full"
                  data-testid="add-to-plan-close-btn"
                >
                  <X size={20} />
                </Button>
              </div>

              {/* Show edited macros summary */}
              {editMode && editedMacros && (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-xs text-yellow-600 font-medium mb-2">Edited Macros:</p>
                  <div className="flex justify-between text-sm">
                    <span>{Math.round(currentMacros.calories)} cal</span>
                    <span>{Math.round(currentMacros.protein)}g P</span>
                    <span>{Math.round(currentMacros.carbs)}g C</span>
                    <span>{Math.round(currentMacros.fat)}g F</span>
                  </div>
                </div>
              )}

              {/* Step 1: Date Selection */}
              {addToPlanStep === 1 && (
                <div>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Choose which day to add this meal to
                  </p>
                  
                  <WeekDateSelector
                    selectedDates={[planDate]}
                    onDatesChange={handleDateSelect}
                    mode="single"
                    allowRange={false}
                    showApplyButton={false}
                  />
                  
                  <Button
                    onClick={handleDateConfirm}
                    disabled={!planDate}
                    className="w-full h-12 rounded-full bg-accent text-white font-semibold mt-4"
                    data-testid="date-confirm-btn"
                  >
                    Continue
                  </Button>
                </div>
              )}

              {/* Step 2: Slot Selection */}
              {addToPlanStep === 2 && (
                <div>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Where should this meal go on{' '}
                    <span className="text-accent font-medium">
                      {new Date(planDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>?
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {mealSlots.map(function(slot) {
                      return (
                        <Button
                          key={slot}
                          onClick={function() { handleSlotSelect(slot); }}
                          disabled={addingToPlan}
                          variant="outline"
                          className="h-14 rounded-xl font-medium text-base"
                          data-testid={'slot-' + slot.toLowerCase()}
                        >
                          {slot}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    onClick={function() { setAddToPlanStep(1); }}
                    variant="ghost"
                    className="w-full h-10 mt-4 text-muted-foreground"
                  >
                    Back to Date Selection
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Replace Confirmation Modal */}
      <AnimatePresence>
        {showReplaceConfirm && existingMealInSlot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={cancelReplace}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl"
              onClick={function(e) { e.stopPropagation(); }}
              data-testid="replace-confirm-modal"
            >
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle size={24} className="text-amber-500" />
                </div>
              </div>
              
              <h3 className="font-heading font-bold text-lg text-center mb-2">
                Replace existing meal?
              </h3>
              
              <p className="text-sm text-muted-foreground text-center mb-6">
                <span className="font-medium text-foreground">{existingMealInSlot.meal_name}</span> is already planned for {selectedMealSlot}. Do you want to replace it?
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={handleReplaceMeal}
                  disabled={addingToPlan}
                  className="w-full h-12 rounded-xl bg-accent text-white font-semibold"
                  data-testid="confirm-replace-btn"
                >
                  {addingToPlan ? 'Replacing...' : 'Replace'}
                </Button>
                <Button
                  onClick={cancelReplace}
                  variant="outline"
                  className="w-full h-12 rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MealView;
